import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { downloadFile } from "@/api";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileSpreadsheet, FileDown, UserCheck, Users as UsersIcon, CalendarClock, AlertTriangle } from "lucide-react";

const STATUSES = ["Present", "Absent", "Half-day", "Leave", "Holiday", "Week-off", "Overtime"];
const CELL = { Present: "bg-success", Absent: "bg-danger", "Half-day": "bg-warning", Leave: "bg-info", Holiday: "bg-primary", "Week-off": "bg-muted-foreground", Overtime: "bg-accent" };
const _todayLabel = () => new Date().toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" });

export default function Attendance() {
  const { user: me } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [staffFilter, setStaffFilter] = useState("");
  const [summary, setSummary] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [staff, setStaff] = useState([]);
  const [records, setRecords] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [manual, setManual] = useState({ user_id: "", date: new Date().toISOString().slice(0, 10), status: "Present", note: "" });
  const [bulk, setBulk] = useState({ user_ids: [], date: new Date().toISOString().slice(0, 10), status: "Holiday", reason: "" });
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ status: "Present", reason: "" });

  const load = useCallback(() => {
    api.get("/attendance/summary", { params: { month } }).then((r) => setSummary(r.data)).catch(() => toast.error("Failed to load summary"));
    api.get("/attendance", { params: { month } }).then((r) => setRecords(r.data)).catch(() => {});
    api.get("/users").then((r) => setStaff(r.data.filter((u) => u.active && u.role !== "super_admin" && !(me.is_workspace_owner && u.id === me.id)))).catch(() => {});
    if (me.role === "super_admin" || me.is_workspace_owner) {
      api.get("/attendance/corrections").then((r) => setCorrections(r.data)).catch(() => setCorrections([]));
    }
  }, [month, me.id, me.is_workspace_owner]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  const daysInMonth = new Date(parseInt(month.slice(0, 4)), parseInt(month.slice(5, 7)), 0).getDate();
  const byUserDay = {};
  records.forEach((r) => { byUserDay[`${r.user_id}|${r.date}`] = r; });
  const visibleStaff = staff.filter((person) => `${person.name} ${person.department || ""} ${person.role}`.toLowerCase().includes(staffFilter.toLowerCase()));

  const markManual = async (e) => {
    e.preventDefault();
    try {
      await api.post("/attendance/manual", manual);
      toast.success("Attendance marked"); setManualOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const markBulk = async (e) => {
    e.preventDefault();
    if (!bulk.user_ids.length) return toast.error("Select at least one staff");
    try {
      const r = await api.post("/attendance/bulk", bulk);
      toast.success(`${r.data.marked} staff marked ${bulk.status}`); setBulkOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const editCell = (rec) => { setEditFor(rec); setEditForm({
    status: rec.status, reason: "",
    check_in: rec.check_in ? new Date(rec.check_in).toISOString().slice(0, 16) : "",
    check_out: rec.check_out ? new Date(rec.check_out).toISOString().slice(0, 16) : "",
  }); };
  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.reason.trim()) return toast.error("Reason is required (logged to audit)");
    try {
      await api.patch(`/attendance/${editFor.id}`, editForm);
      toast.success("Attendance updated"); setEditFor(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const decideCorrection = async (correction, decision) => {
    const reason = window.prompt(`Optional review note for ${decision}:`, "") ?? "";
    try {
      await api.post(`/attendance/corrections/${correction.id}/${decision}`, { reason });
      toast.success(`Correction ${decision}d`);
      load();
    } catch (error) { toast.error(error.response?.data?.detail || "Correction review failed"); }
  };

  return (
    <div className="space-y-5" data-testid="attendance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Attendance</h1>
          <p className="text-sm text-muted-foreground">Check-ins, late flags, monthly grid, bulk marking</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Input type="month" data-testid="att-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9" />
          <Input aria-label="Filter staff" data-testid="att-staff-filter" placeholder="Filter staff" value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} className="w-36 h-9" />
          <Button variant="outline" data-testid="att-manual-button" onClick={() => setManualOpen(true)}><UserCheck size={15} className="mr-1.5" /> Mark Manually</Button>
          <Button variant="outline" data-testid="att-bulk-button" onClick={() => setBulkOpen(true)}><UsersIcon size={15} className="mr-1.5" /> Bulk Mark</Button>
          <Button variant="outline" data-testid="att-export-button" onClick={() => downloadFile(`/attendance/export.xlsx?month=${month}`, `attendance-${month}.xlsx`).then(() => toast.success("Excel exported")).catch(() => toast.error("Export failed"))}><FileSpreadsheet size={15} className="mr-1.5" /> Excel</Button>
          <Button variant="outline" data-testid="att-export-pdf-button" onClick={() => downloadFile(`/attendance/export.pdf?month=${month}`, `attendance-${month}.pdf`).then(() => toast.success("PDF exported")).catch(() => toast.error("Export failed"))}><FileDown size={15} className="mr-1.5" /> PDF</Button>
        </div>
      </div>

      {summary?.today_counts && (
        <section className="space-y-3" data-testid="attendance-live-board">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-base font-semibold">Today · {_todayLabel()}</h2>
            <span className="text-xs text-muted-foreground">Refreshes every 30 seconds</span>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {Object.entries(summary.today_counts).map(([label, count]) => (
              <Card key={label} className="p-3" data-testid={`attendance-today-${label.toLowerCase().replaceAll(" ", "-")}`}>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="mt-1 font-mono text-xl font-bold">{count}</div>
              </Card>
            ))}
          </div>
          <div className="overflow-x-auto border bg-card">
            <table className="w-full min-w-[760px] text-xs" data-testid="attendance-today-list">
              <thead className="bg-muted/50 text-left uppercase text-muted-foreground">
                <tr><th className="px-3 py-2">Staff</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Check in</th><th className="px-3 py-2">Check out</th><th className="px-3 py-2">Hours</th><th className="px-3 py-2">Location</th><th className="px-3 py-2">Selfie</th></tr>
              </thead>
              <tbody>
                {(summary.today_board || []).map((person) => (
                  <tr key={person.user_id} className="border-t" data-testid={`attendance-today-row-${person.user_id}`}>
                    <td className="px-3 py-2"><div className="font-medium">{person.user_name || person.name}</div><div className="text-muted-foreground">{person.department || person.role}</div></td>
                    <td className="px-3 py-2">{person.board_status}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{person.check_in ? new Date(person.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{person.check_out ? new Date(person.check_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="px-3 py-2">{person.total_hours ? `${person.total_hours}h` : "—"}</td>
                    <td className="px-3 py-2">{(person.location?.lat ?? person.lat) != null ? `${person.location?.lat ?? person.lat}, ${person.location?.lng ?? person.lng}` : "—"}</td>
                    <td className="px-3 py-2">{person.selfie ? <a className="text-primary underline" href={person.selfie} target="_blank" rel="noreferrer">View</a> : "—"}</td>
                  </tr>
                ))}
                {!summary.today_board?.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No attendance records for today</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {(me.role === "super_admin" || me.is_workspace_owner) && corrections.length > 0 && (
        <section className="space-y-3" data-testid="attendance-corrections">
          <h2 className="font-heading text-base font-semibold">Correction requests</h2>
          <div className="divide-y border bg-card">
            {corrections.map((correction) => (
              <div key={correction.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`attendance-correction-${correction.id}`}>
                <div className="min-w-48 flex-1">
                  <div className="text-sm font-medium">{correction.user_name} · {correction.date}</div>
                  <div className="text-xs text-muted-foreground">{correction.reason}</div>
                  <div className="mt-1 text-xs">Requested: {Object.entries(correction.requested || {}).map(([key, value]) => `${key.replace("_", " ")} ${value}`).join(" · ")}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => decideCorrection(correction, "reject")}>Reject</Button>
                <Button size="sm" onClick={() => decideCorrection(correction, "approve")} className="bg-success text-inverse hover:bg-success/90">Approve</Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 rounded-2xl" data-testid="att-present-pct"><div className="text-xs uppercase tracking-widest text-muted-foreground">Present %</div><div className="font-mono text-2xl font-bold mt-1 text-success">{summary.present_pct}%</div></Card>
          <Card className="p-4 rounded-2xl" data-testid="att-late-total"><div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1"><CalendarClock size={12} /> Late check-ins</div><div className="font-mono text-2xl font-bold mt-1 text-warning-foreground">{summary.late_total}</div></Card>
          <Card className="p-4 rounded-2xl" data-testid="att-absentees">
            <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1"><AlertTriangle size={12} /> Top absentees</div>
            <div className="text-sm mt-1 space-y-0.5">{summary.top_absentees.filter((a) => a.absent > 0).map((a) => <div key={a.user_id} className="flex justify-between"><span>{a.name}</span><b className="font-mono text-danger-foreground">{a.absent}</b></div>)}</div>
            {!summary.top_absentees.some((a) => a.absent > 0) && <div className="text-sm text-muted-foreground mt-1">No absences 🎉</div>}
          </Card>
          <Card className="p-4 rounded-2xl" data-testid="att-departments">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">By department</div>
            <div className="text-sm mt-1 space-y-0.5">{summary.by_department.map((d) => <div key={d.department} className="flex justify-between"><span className="truncate">{d.department}</span><span className="font-mono text-xs text-muted-foreground">{d.present}P/{d.absent}A</span></div>)}</div>
          </Card>
        </div>
      )}

      <Card className="overflow-x-auto rounded-2xl">
        <table className="text-xs" data-testid="attendance-grid">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
              <th className="px-3 py-2 sticky left-0 bg-card z-10 min-w-36">Staff</th>
              {Array.from({ length: daysInMonth }, (_, i) => <th key={i} className="px-1 py-2 text-center w-6">{i + 1}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibleStaff.map((s) => (
              <tr key={s.id} className="border-b last:border-0" data-testid={`att-row-${s.id}`}>
                <td className="px-3 py-1.5 sticky left-0 bg-card z-10">
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="text-[10px] text-muted-foreground">{s.department || s.role}</div>
                </td>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const d = `${month}-${String(i + 1).padStart(2, "0")}`;
                  const rec = byUserDay[`${s.id}|${d}`];
                  return (
                    <td key={i} className="px-0.5 py-1.5 text-center">
                      {rec ? (
                        <button data-testid={`att-cell-${s.id}-${d}`} onClick={() => editCell(rec)} title={`${rec.status}${rec.late ? " (late)" : ""} — click to edit`}
                          className={`w-5 h-5 rounded ${CELL[rec.status] || "bg-primary-tint"} ${rec.late ? "ring-2 ring-amber-400" : ""} inline-block`} />
                      ) : <span className="w-5 h-5 inline-block rounded bg-muted/60" />}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!visibleStaff.length && <tr><td colSpan={daysInMonth + 1} className="px-4 py-8 text-center text-muted-foreground">No staff found</td></tr>}
          </tbody>
        </table>
        <div className="flex gap-3 px-4 py-3 border-t flex-wrap">
          {STATUSES.map((s) => <span key={s} className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className={`w-3 h-3 rounded ${CELL[s]}`} />{s}</span>)}
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="w-3 h-3 rounded bg-success ring-2 ring-warning" />Late</span>
        </div>
      </Card>

      <Dialog open={!!editFor} onOpenChange={() => setEditFor(null)}>
        <DialogContent data-testid="att-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit — {editFor?.user_name} · {editFor?.date}</DialogTitle>
            <DialogDescription>Change the status and record a reason (saved to the audit log).</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
              <SelectTrigger data-testid="att-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Check in</label><Input type="datetime-local" data-testid="att-edit-check-in" value={editForm.check_in || ""} onChange={(e) => setEditForm({ ...editForm, check_in: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Check out</label><Input type="datetime-local" data-testid="att-edit-check-out" value={editForm.check_out || ""} onChange={(e) => setEditForm({ ...editForm, check_out: e.target.value })} /></div>
            </div>
            <Input data-testid="att-edit-reason" placeholder="Reason for edit (required, logged)" value={editForm.reason} onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} required />
            <Button data-testid="att-edit-save" type="submit" className="w-full bg-primary hover:bg-primary-hover">Save Correction</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="att-manual-dialog" aria-describedby="att-manual-desc">
          <DialogHeader>
            <DialogTitle className="font-heading">Mark Attendance Manually</DialogTitle>
            <DialogDescription id="att-manual-desc">Mark or correct one staff member's status for a date.</DialogDescription>
          </DialogHeader>
          <form onSubmit={markManual} className="space-y-3">
            <Select value={manual.user_id} onValueChange={(v) => setManual({ ...manual, user_id: v })}>
              <SelectTrigger data-testid="att-manual-user"><SelectValue placeholder="Select staff…" /></SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.role})</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <Input type="date" data-testid="att-manual-date" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} required />
              <Select value={manual.status} onValueChange={(v) => setManual({ ...manual, status: v })}>
                <SelectTrigger data-testid="att-manual-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
              <Input data-testid="att-manual-note" placeholder="Reason (required, logged)" value={manual.note} onChange={(e) => setManual({ ...manual, note: e.target.value })} required />
            <Button data-testid="att-manual-save" type="submit" className="w-full bg-primary hover:bg-primary-hover">Mark</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="att-bulk-dialog" aria-describedby="att-bulk-desc">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Mark Attendance</DialogTitle>
            <DialogDescription id="att-bulk-desc">Apply one status to multiple staff for a single date.</DialogDescription>
          </DialogHeader>
          <form onSubmit={markBulk} className="space-y-3">
            <div className="max-h-44 overflow-y-auto border rounded-lg p-2 space-y-1">
              {staff.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-accent cursor-pointer">
                  <input type="checkbox" data-testid={`att-bulk-user-${s.id}`} checked={bulk.user_ids.includes(s.id)}
                    onChange={() => setBulk({ ...bulk, user_ids: bulk.user_ids.includes(s.id) ? bulk.user_ids.filter((x) => x !== s.id) : [...bulk.user_ids, s.id] })} />
                  {s.name} <span className="text-xs text-muted-foreground">({s.department || s.role})</span>
                </label>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input type="date" data-testid="att-bulk-date" value={bulk.date} onChange={(e) => setBulk({ ...bulk, date: e.target.value })} required />
              <Select value={bulk.status} onValueChange={(v) => setBulk({ ...bulk, status: v })}>
                <SelectTrigger data-testid="att-bulk-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input data-testid="att-bulk-reason" placeholder="Reason (required, logged)" value={bulk.reason} onChange={(e) => setBulk({ ...bulk, reason: e.target.value })} required />
            <Button data-testid="att-bulk-save" type="submit" className="w-full bg-primary hover:bg-primary-hover">Mark {bulk.user_ids.length} staff</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
